import { useContext, useMemo } from 'react';

import { SizeChartDisplayResult, WidgetContentEntry, WidgetEntry, WidgetEntryType } from '@vestico/model';

import { ProductContext } from '../../MainContext';
import { ConfigContext } from '../../WidgetEventsManager';
import { AvailableMatchingType } from '../../model/matchingType';

export interface UseShowEntryProps {
  entry?: WidgetEntry;
}

const getShowEntry = (
  entry: WidgetEntry,
  postsWithMatchInfo: number,
  postsWithoutMatchInfo: number,
  sizeRecommendationAvailable: boolean,
  unmatchable: boolean,
  sizeChart?: SizeChartDisplayResult,
  isQuiz?: boolean
) => {
  const minimumNumberOfVerifiedPosts = entry.minimumNumberOfVerifiedPosts ?? 1;
  const hasEnoughPostsWithMatchInfo = postsWithMatchInfo >= minimumNumberOfVerifiedPosts;
  const { type, location } = entry;
  switch (type) {
    case WidgetEntryType.button:
    case WidgetEntryType.link:
      return !!isQuiz || (!unmatchable && hasEnoughPostsWithMatchInfo) || sizeRecommendationAvailable || sizeChart?.unmatched;
    case WidgetEntryType.imagePreview:
      return !unmatchable && hasEnoughPostsWithMatchInfo;
    case WidgetEntryType.content: {
      const contentEntry = entry as WidgetContentEntry;
      const { filter } = contentEntry;
      return location === 'pdps' && filter !== 'manual' ? postsWithMatchInfo + postsWithoutMatchInfo >= minimumNumberOfVerifiedPosts : true;
    }

    default:
      throw new Error(`Unhandled entry type ${type}`);
  }
};

const getUseSizeRecommendationOnly = (
  entry: WidgetEntry,
  postsWithMatchInfo: number,
  sizeRecommendationAvailable: boolean,
  sizeChart?: SizeChartDisplayResult
) => {
  const minimumNumberOfVerifiedPosts = entry.minimumNumberOfVerifiedPosts ?? 1;
  const { type } = entry;
  switch (type) {
    case WidgetEntryType.button:
    case WidgetEntryType.link:
      return (sizeRecommendationAvailable || sizeChart?.unmatched) && postsWithMatchInfo < minimumNumberOfVerifiedPosts;
    case WidgetEntryType.imagePreview:
    case WidgetEntryType.content:
      return false;
    default:
      throw new Error(`Unhandled entry type ${type}`);
  }
};

export const useShowEntry = ({ entry }: UseShowEntryProps) => {
  const {
    postsWithUserMatchInfo: postsWithMatchInfo,
    postsWithoutUserMatchInfo: postsWithoutMatchInfo,
    sizeRecommendationAvailable,
    unmatchable,
    sizeChart,
    anyProductHasSizeRecommendationAvailable,
  } = useContext(ProductContext);

  const { isQuiz } = useContext(ConfigContext);
  const showEntry = useMemo(
    () =>
      !!entry &&
      getShowEntry(
        entry,
        postsWithMatchInfo,
        postsWithoutMatchInfo,
        sizeRecommendationAvailable || anyProductHasSizeRecommendationAvailable,
        unmatchable,
        sizeChart,
        isQuiz
      ),
    [
      entry,
      postsWithMatchInfo,
      postsWithoutMatchInfo,
      sizeRecommendationAvailable,
      anyProductHasSizeRecommendationAvailable,
      unmatchable,
      sizeChart,
      isQuiz,
    ]
  );
  const useSizeRecommendationOnly = useMemo(
    () =>
      !!entry &&
      getUseSizeRecommendationOnly(entry, postsWithMatchInfo, sizeRecommendationAvailable || anyProductHasSizeRecommendationAvailable, sizeChart),
    [entry, postsWithMatchInfo, sizeRecommendationAvailable, anyProductHasSizeRecommendationAvailable, sizeChart]
  );

  const availableMatchingTypes = useMemo(() => {
    const types: AvailableMatchingType[] = [];
    if (showEntry) {
      if (!useSizeRecommendationOnly) {
        types.push('content');
      }
      if (useSizeRecommendationOnly || (sizeRecommendationAvailable && !unmatchable)) {
        types.push('size-rec');
      }
      if (isQuiz) {
        types.push('quiz');
      }
    }
    return types;
  }, [isQuiz, showEntry, sizeRecommendationAvailable, unmatchable, useSizeRecommendationOnly]);

  return { showEntry, availableMatchingTypes };
};
